import { useState } from 'react'
import { PLANS, PLAN_RANK, getMergedPlan } from '../constants/plans'
import { switchPlan } from '../services/manage'
import { fetchUserData, showMessageModal } from '../Manage/actions'
import { ERROR_CODE_MAP } from '../stripe.constants'
import { getPlanIdByFeature } from '../utils/general'

export const useSwitchPlan = ({
  isOnFreeTrial,
  liveSites,
  productId,
  currency,
  interval,
  email,
  dispatch,
  setCancelTrialModalShow,
  onCancel
}) => {
  const [switchPlanSuggestionModalShow, setSwitchPlanSuggestionModalShow] = useState(false)
  const [confirmSwitchPlanModalShow, setconfirmSwitchPlanModalShow] = useState(false)
  const [switchPlanProcessing, setSwitchPlanProcessing] = useState(false)
  const [eligiblePlanWithPrices, setEligiblePlanWithPrices] = useState(null)
  const [currentPlanWithPrices, setCurrentPlanWithPrices] = useState(null)

  const getLowestEligiblePlan = () => {
    if (!liveSites) return null

    const projects = liveSites.length
    const fileSize = projects ? Math.max(...liveSites.map((s) => s.size || 0)) : 0
    const customDomains = projects ? liveSites.filter((s) => s.isCustomDomain).length : 0

    const requiredPlanId = getPlanIdByFeature({ fileSize, projects, customDomains })

    const currentPlanRank = PLAN_RANK[productId] || PLAN_RANK[PLANS.PRO.id]

    // Return the required plan if it's strictly lower ranked than the current plan
    if (PLAN_RANK[requiredPlanId] < currentPlanRank) {
      return Object.values(PLANS).find((plan) => plan.id === requiredPlanId)
    }

    return null
  }

  const onContinueCancellation = () => {
    setSwitchPlanSuggestionModalShow(false)
    if (isOnFreeTrial) {
      setCancelTrialModalShow(true)
    } else if (onCancel) {
      onCancel()
    }
  }

  const handleCancelClick = () => {
    const eligiblePlan = getLowestEligiblePlan()
    if (eligiblePlan) {
      setEligiblePlanWithPrices(getMergedPlan(eligiblePlan.id, currency))

      const currentPlan = Object.values(PLANS).find((p) => p.id === productId) || PLANS.PRO
      const currentPlanWithPricing = getMergedPlan(currentPlan.id, currency)
      if (currentPlanWithPricing) {
        setCurrentPlanWithPrices(currentPlanWithPricing)
      }

      setSwitchPlanSuggestionModalShow(true)
    } else {
      onContinueCancellation()
    }
  }

  const exceedsFreePlanLimits =
    Array.isArray(liveSites) && (liveSites.length > 1 || liveSites.some((s) => s.isCustomDomain))

  const handleConfirmSwitchPlanClick = () => {
    setSwitchPlanSuggestionModalShow(false)
    setconfirmSwitchPlanModalShow(true)
  }

  const instantSwitchPlan = () => {
    const period = interval === 'year' || interval === 'yearly' ? 'yearly' : 'monthly'
    const selectedPlanId = eligiblePlanWithPrices?.id
    const selectedPlanLabel = eligiblePlanWithPrices?.label
    const selectedPriceId = eligiblePlanWithPrices?.prices?.[period]?.stripe?.priceId

    if (!selectedPlanId || !selectedPlanLabel || !selectedPriceId) {
      dispatch(
        showMessageModal({
          title: 'Error',
          message: 'Unable to determine the selected plan. Please try again.',
          success: false
        })
      )
      setconfirmSwitchPlanModalShow(false)
      return
    }

    setSwitchPlanProcessing(true)

    const formData = {
      email,
      productId: selectedPlanId,
      upgradeReason: 'Switch Plan',
      priceId: selectedPriceId,
      plan: selectedPlanLabel,
      ...(isOnFreeTrial && { endTrial: true }),
      isConvertingToSuggestedPlan: true
    }

    switchPlan(formData)
      .then((res) => {
        setSwitchPlanProcessing(false)
        setconfirmSwitchPlanModalShow(false)
        dispatch(fetchUserData(false))
        dispatch(showMessageModal({ title: 'Plan updated' }))
      })
      .catch((error) => {
        setSwitchPlanProcessing(false)
        console.error(error)
        const errorMessage = ERROR_CODE_MAP[error?.declineCode] || 'Failed to switch plan. Please try again.'
        dispatch(
          showMessageModal({
            title: 'Error',
            message: errorMessage,
            success: false
          })
        )
      })
  }

  return {
    switchPlanSuggestionModalShow,
    setSwitchPlanSuggestionModalShow,
    confirmSwitchPlanModalShow,
    setconfirmSwitchPlanModalShow,
    switchPlanProcessing,
    eligiblePlanWithPrices,
    currentPlanWithPrices,
    handleCancelClick,
    handleCancelTrialClick: handleCancelClick,
    handleConfirmSwitchPlanClick,
    onContinueCancellation,
    instantSwitchPlan,
    exceedsFreePlanLimits
  }
}
